/**
 * Vehicle layout configuration system
 *
 * This manages which layout image to use based on the vehicle model's layout ID.
 * Each vehicle model has a layoutId that corresponds to a specific layout configuration.
 * This approach ensures that historical vehicle issues always display correctly
 * with their original layout image, even when new layouts are introduced.
 */

export interface LayoutConfig {
  image: string;
  name: string;
  category: string;
  deprecated: boolean;
  description?: string;
  dimensions?: {
    width: number;
    height: number;
  };
}

// FIXME: This is made by Claude Code as an initial draft. We need to re-organize this later.
export const LAYOUT_CONFIGS: Record<string, LayoutConfig> = {
  regular: {
    image: "vehicle-layout-regular.png",
    name: "Regular Car",
    category: "regular",
    deprecated: false,
    description: "Standard passenger car layout",
    dimensions: {
      width: 1072,
      height: 388,
    },
  },
  // Additional layout examples (commented out as images don't exist yet)
  formula: {
    image: "vehicle-layout-formula.png", // Using existing image as placeholder
    name: "Formula Car",
    category: "formula",
    deprecated: false,
    description: "Single-seater racing car layout",
    dimensions: {
      width: 2696,
      height: 812,
    },
  },
} as const;

/**
 * Get layout configuration for a specific layout ID
 */
export const getLayoutConfig = (layoutId?: string): LayoutConfig => {
  const id = layoutId || "regular"; // Default to regular
  const config = LAYOUT_CONFIGS[id];

  if (!config) {
    console.warn(`Unknown layout ID: ${layoutId}, falling back to regular`);
    return LAYOUT_CONFIGS["regular"];
  }

  return config;
};

/**
 * Get the default layout ID for new vehicles
 */
export const getDefaultLayoutId = (): string => {
  return "regular";
};

/**
 * Get all available layout configurations
 */
export const getAllLayoutConfigs = (): Array<{
  layoutId: string;
  config: LayoutConfig;
}> => {
  return Object.entries(LAYOUT_CONFIGS).map(([layoutId, config]) => ({
    layoutId,
    config,
  }));
};

/**
 * Get layout configurations by category
 */
export const getLayoutConfigsByCategory = (
  category: string,
): Array<{ layoutId: string; config: LayoutConfig }> => {
  return getAllLayoutConfigs().filter(
    ({ config }) => config.category === category,
  );
};

/**
 * Get all available categories
 */
export const getLayoutCategories = (): string[] => {
  const categories = new Set(
    Object.values(LAYOUT_CONFIGS).map((config) => config.category),
  );
  return Array.from(categories).sort();
};
